<script lang="typescript">
    import { state, setState } from "./stores.js";
    import { MessageType } from "./types";
    import type { LensObject, Lens, LensVariant, LensOption } from "./types";
    import { computeTotalPrice, getBaseLensObject, getBaseLensImage, getLogo } from "./utils";
    import { push } from "svelte-spa-router";
    import { onDestroy } from "svelte";
    import LensTierHeader from "./LensTierHeader.svelte";
    import LensCardLarge from "./LensCardLarge.svelte";
    import LensOptionsCard from "./LensOptionsCard.svelte";
    import { lensOption, logoImages, upgradeLensImages } from "./appData";

    export let showUpgradeTiers: boolean = false;

    if ($state.rxId === null) {
        window.location.replace("/");
    }

    // We need both variants to be null before progressing from this page
    // 1. User selects primary variant
    // 2. User selects secondary variant
    //    - state now has values for primaryVariantIndex and secondaryVariantIndex
    // 3. User hits back twice from results page to end up back here
    //    - state is as above
    // 4. User selects primary variant with no secondary variant options
    //    - state.primaryVariantIndex is updated, secondaryVariantIndex remains
    // This invalid state will likely cause a crash on the results page.
    setState({ message: MessageType.RESET_VARIANTS });

    const getDetailURL = (variantOption: LensOption): string => {
        switch (variantOption) {
            case lensOption.BLUE:
                return "/detail/easy-clean-plus-blue/";
            case lensOption.PLUS:
                return "/detail/anti-reflection-plus/";
            case lensOption.POLARISED:
                return "/detail/polarised/";
            case lensOption.ANTIFOG:
                return "/detail/anti-fog/";
            case "Anti-Fog + Adaptive":
                return "/detail/anti-fog-adaptive/";
            case lensOption.TINT_UV:
                return "/detail/tint-uv/";
            case lensOption.ADAPTIVE:
                return "/detail/adaptive";
            case lensOption.TRANSITIONS:
                return "/detail/transitions";
            case lensOption.TRANSITIONS_XTRA:
                return "/detail/transitions-xtractive";
            default:
                return "/";
        }
    };

    // const selectPrimaryVariant = (variantIndex: number, variantName: string): void => {
    const selectPrimaryVariant = (variantIndex: number, optionType: LensOption): void => {
        setState({
            message: MessageType.SET_PRIMARY_VARIANT,
            primaryVariantIndex: variantIndex,
        });
        push(getDetailURL(optionType));
    };

    const orderButtonHandler = () => {
        setState({ message: MessageType.RESET_VARIANTS });
        push("/results/");
    };

    const lensObject: LensObject = getBaseLensObject($state.focalType);

    let baseLens: Lens;
    $: baseLens = lensObject[$state.baseLensKey];

    let variants: LensVariant[];

    // sort variants first by price then by title
    $: variants = lensObject[$state.baseLensKey].variants
        .map((variant, i) => ({ ...variant, variantIndex: i }))
        .sort((a, b) => {
            let priceA = computeTotalPrice(lensObject, $state.baseLensKey, a, null, $state.region);
            let priceB = computeTotalPrice(lensObject, $state.baseLensKey, b, null, $state.region);

            if (priceA > priceB) {
                return 1;
            } else if (priceA < priceB) {
                return -1;
            }

            if (a.name > b.name) {
                return 1;
            } else if (a.name < b.name) {
                return -1;
            }
        });

    const getPrice = (variant: LensVariant): number =>
        computeTotalPrice(lensObject, $state.baseLensKey, variant, null, $state.region);

    const getLensImage = (lens: Lens) => {
        if (showUpgradeTiers) {
            return upgradeLensImages[$state.lensTier].src;
        }
        return getBaseLensImage(lens, $state.region);
    };

    $: image = getLensImage(baseLens);

    onDestroy(() => window.scrollTo(0, 0));
</script>

<LensTierHeader {showUpgradeTiers} />
<section>
    <div class="page-container pb-16 px-8">
        <LensCardLarge onClick={orderButtonHandler} selectedLens={baseLens} {image} />

        {#if variants.length > 0}
            <h2 class="mt-16 text-3xl font-normal mx-auto">Enhance your lenses</h2>
            <p class="mt-4 w-auto text-lg mx-auto">
                Discover a range of additional options to further enhance your lenses.
            </p>
            <ul class="mx-4 lg:mx-auto lg:w-3/4 space-y-12 mx-auto mt-16 list-none">
                {#each variants as variant}
                    <LensOptionsCard
                        {variant}
                        price={getPrice(variant)}
                        logo={getLogo(variant.optionType)}
                        onButtonClick={() =>
                            selectPrimaryVariant(variant.variantIndex, variant.optionType)}
                    />
                {/each}
            </ul>
        {/if}
    </div>
</section>
